import { Injectable } from '@angular/core';
import { TokenResponse, AuthVerifierParams, UserSessionResponse } from '@airtradeit/authentication';

import { LocalStorageEnum } from '@enums/local-storage.enum';
import { AffiliateOption } from '../affiliate/_interfaces_/affiliate-options.interface';
import { SearchRequestBody } from '../search-form/_interfaces';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  constructor() {}

  public getCulture(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_culture);
  }

  public setCulture(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_culture, value);
  }

  public getUserSessionToken(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_userSessionToken);
  }

  public setUserSessionToken(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_userSessionToken, value);
  }

  public getUserSessionUrl(): string | null {
    const url = localStorage.getItem(LocalStorageEnum.OM_userSessionURL);
    return this.http2https(url);
  }

  public clearSession(): void {
    localStorage.removeItem(LocalStorageEnum.OM_bearerToken);
    localStorage.removeItem(LocalStorageEnum.OM_tokenExpiry);
    localStorage.removeItem(LocalStorageEnum.OM_refreshToken);
    localStorage.removeItem(LocalStorageEnum.OM_userSessionToken);
    localStorage.removeItem(LocalStorageEnum.OM_userSessionURL);
    localStorage.removeItem(LocalStorageEnum.OM_authVerifierParams);
  }

  public http2https(url: string): string | null {
    let ret;
    if (url) {
      ret = url.includes('http') ? url.replace(/^http:\/\//i, 'https://') : url;
    } else {
      ret = null;
    }
    return ret;
  }

  public getBearerToken(): string | null {
    return this.getBearerTokenValue(true);
  }

  public getBearerTokenValue(withTokenType?: boolean): string | null {
    const bearerToken = JSON.parse(localStorage.getItem(LocalStorageEnum.OM_bearerToken));
    let tokenValue = null;
    if (bearerToken !== null) {
      tokenValue = `${withTokenType ? bearerToken.token_type + ' ' : ''}${bearerToken.access_token}`;
    }
    return tokenValue;
  }

  public getAuthVerifierParams(): AuthVerifierParams {
    return JSON.parse(localStorage.getItem(LocalStorageEnum.OM_authVerifierParams));
  }

  public getFlightResultListUrl(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_flightResultListUrl);
  }

  public getSelectedFlightUrl(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_selectedflightUrl);
  }

  public getChangedFlightUrl(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_changedFlightUrl);
  }

  public getFilterOptionsUrl(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_filterOptionsUrl);
  }

  public getRetrievedFlightIdentifier(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_retrievedFlightIdentifier);
  }

  public getOrderId(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_orderId);
  }

  public setUserSessionUrl(response: UserSessionResponse): void {
    const userSessionUrl = response.Data;
    const userSessionToken = userSessionUrl.substr(userSessionUrl.lastIndexOf('/') + 1);
    localStorage.setItem(LocalStorageEnum.OM_userSessionURL, userSessionUrl);
    this.setUserSessionToken(userSessionToken);
  }

  public setBearerToken(response: TokenResponse): void {
    localStorage.setItem(
      LocalStorageEnum.OM_bearerToken,
      JSON.stringify({
        access_token: response.token,
        token_type: 'Bearer',
      })
    );
  }

  public setTokenExpiry(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_tokenExpiry, value);
  }
  public getTokenExpiry(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_tokenExpiry);
  }

  public setRefreshToken(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_refreshToken, value);
  }
  public getRefreshToken(): string | null {
    return localStorage.getItem(LocalStorageEnum.OM_refreshToken);
  }

  public setAuthVerifierParams(value: AuthVerifierParams): void {
    const auth = JSON.stringify(value);
    localStorage.setItem(LocalStorageEnum.OM_authVerifierParams, auth);
  }

  public setSelectedFlightUrl(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_selectedflightUrl, value);
  }

  public setChangedFlightUrl(value: string): void {
    localStorage.setItem(LocalStorageEnum.OM_changedFlightUrl, value);
  }

  public removeFlightResultListUrl() {
    localStorage.removeItem(LocalStorageEnum.OM_flightResultListUrl);
  }

  public setRetrievedFlightIdentifier(value: string) {
    localStorage.setItem(LocalStorageEnum.OM_retrievedFlightIdentifier, value);
  }

  public setOrderId(value: string) {
    localStorage.setItem(LocalStorageEnum.OM_orderId, value);
  }

  public setSelectedAffiliate(value: string) {
    localStorage.setItem(LocalStorageEnum.OM_selectedAffiliate, value);
  }

  public getSelectedAffiliate(): AffiliateOption {
    return JSON.parse(localStorage.getItem(LocalStorageEnum.OM_selectedAffiliate));
  }

  public setFlightSearchPayload(payload: SearchRequestBody) {
    const parsedPayload = JSON.stringify(payload);
    localStorage.setItem(LocalStorageEnum.OM_changedFlightPayload, parsedPayload);
  }

  public getFlightSearchPayload(): SearchRequestBody {
    return JSON.parse(localStorage.getItem(LocalStorageEnum.OM_changedFlightPayload));
  }

  public setOrderReference(value: string) {
    localStorage.setItem(LocalStorageEnum.OM_orderReference, value);
  }

  public getOrderReference() {
    return localStorage.getItem(LocalStorageEnum.OM_orderReference);
  }
}
